import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const TOAST_DURATION_MS = 5000;

const ToastContext = createContext(null);

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timeoutRefs = useRef(new Map());

  const removeToast = useCallback((id) => {
    const tid = timeoutRefs.current.get(id);
    if (tid) {
      clearTimeout(tid);
      timeoutRefs.current.delete(id);
    }
    setToasts((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const addToast = useCallback(
    (message, variant = 'info') => {
      if (!message) {
        return null;
      }
      const id =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setToasts((prev) => [...prev, { id, message, variant }]);
      const timerId = setTimeout(() => {
        removeToast(id);
      }, TOAST_DURATION_MS);
      timeoutRefs.current.set(id, timerId);
      return id;
    },
    [removeToast],
  );

  useEffect(
    () => () => {
      timeoutRefs.current.forEach((tid) => clearTimeout(tid));
      timeoutRefs.current.clear();
    },
    [],
  );

  const value = useMemo(
    () => ({
      toasts,
      addToast,
      removeToast,
    }),
    [toasts, addToast, removeToast],
  );

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
};

export const useToastContext = () => useContext(ToastContext);
